'use client';

import { useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { getAuthToken } from '@/utils/auth';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || '';

/**
 * Watch live match scores for the signed-in user's school.
 *
 * Returns a map of `fixtureId -> fixture` carrying only what has arrived
 * over the socket since mount. Callers overlay it on whatever they fetched
 * over HTTP, so a page still renders correctly with the socket down — the
 * live layer is an enhancement, never the source of truth.
 *
 * The school is derived server-side from the caller's own token: the client
 * asks to join and gets its own tenant's room, so there is no id to pass
 * (and none to tamper with).
 *
 * @param {boolean} enabled Skip connecting entirely — e.g. while the user is
 *        on a tab that shows no fixtures.
 */
export function useLiveScores(enabled = true, { fetchAll = false } = {}) {
  const [live, setLive] = useState({});
  const [connected, setConnected] = useState(false);
  // Bumped whenever a match finishes. A list filtered on "upcoming" should
  // drop it, and only a refetch knows that — merging alone would leave a
  // completed match sitting in the list until the next page load.
  const [endedCount, setEndedCount] = useState(0);
  const socketRef = useRef(null);

  // Seed from the open scoreboard so a viewer who arrives MID-match sees it
  // immediately, instead of waiting for the next point to be scored.
  useEffect(() => {
    if (!enabled || !fetchAll) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { apiGet } = await import('@/lib/apiClient');
        const res = await apiGet('/api/sports/live');
        if (cancelled) return;
        const seeded = {};
        (res?.matches || []).forEach((m) => { seeded[m.id] = m; });
        // Merge UNDER anything the socket already delivered — a push that
        // landed while this request was in flight is the newer truth.
        setLive((prev) => ({ ...seeded, ...prev }));
      } catch {
        // A school without the sports feature 403s here. The dock simply
        // stays empty; it must never break the page it sits on.
      }
    })();
    return () => { cancelled = true; };
  }, [enabled, fetchAll]);

  useEffect(() => {
    if (!enabled) return undefined;
    const token = getAuthToken();
    if (!token) return undefined;

    const socket = io(API_BASE_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    // Re-join on every connect, not just the first — after a dropped
    // connection Socket.IO gives us a NEW session that is in no rooms, so a
    // one-shot join would leave the user silently watching nothing.
    const join = () => {
      setConnected(true);
      socket.emit('sports:live:join');
    };
    socket.on('connect', join);
    socket.on('disconnect', () => setConnected(false));
    socket.on('sports:live:score', ({ fixture }) => {
      if (!fixture?.id) return;
      // Keep ENDED matches too. They're filtered out of `matches` below so
      // they leave the dock, but `merge` still needs them: dropping the
      // entry made merge fall back to the row fetched over HTTP, which
      // still said isLive — so a finished match stayed "Live" in the
      // fixtures table until the page was reloaded.
      setLive((prev) => {
        if (prev[fixture.id]?.isLive && !fixture.isLive) setEndedCount((n) => n + 1);
        return { ...prev, [fixture.id]: fixture };
      });
    });

    return () => {
      socket.emit('sports:live:leave');
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [enabled]);

  /**
   * Newest known version of a fixture: the live one if we've seen it.
   *
   * "Newest" is not the same as "from the socket". A result recorded over
   * HTTP — the quick-result modal or the edit form — completes a fixture,
   * and the caller refetches straight after. Preferring the socket copy
   * blindly then handed back the row from BEFORE the whistle, so a match
   * that had been scored live went on showing as in-play (with no result)
   * until the page was reloaded, which is exactly what a reload "fixed"
   * by emptying this map. A fetched row that says Completed is the later
   * truth, so it wins.
   */
  const merge = (fixture) => {
    if (!fixture?.id) return fixture;
    const seen = live[fixture.id];
    if (!seen) return fixture;
    if (fixture.status === 'Completed' && seen.status !== 'Completed') return fixture;
    return seen;
  };

  // TODAY's board: in play, plus finished-today with the final result. A
  // match ending is when people go looking for the score, so it stays.
  // Everything here either arrived from /api/sports/live (already limited
  // to today) or went live during this session.
  const matches = Object.values(live)
    .filter((m) => m.isLive || m.status === 'Completed')
    .sort((a, b) => {
      // Live first — a finished score can wait behind one still moving.
      if (a.isLive !== b.isLive) return a.isLive ? -1 : 1;
      if (a.isLive) {
        // Oldest kick-off first, so the order stays stable as scores change.
        return new Date(a.liveStartedAt || 0) - new Date(b.liveStartedAt || 0);
      }
      return new Date(b.completedAt || 0) - new Date(a.completedAt || 0);
    });

  return { live, matches, connected, merge, endedCount };
}

export default useLiveScores;
